/**
 * شاشة «الليدز» — طلبات التسجيل (design/Leads.dc.html).
 *
 * 🔴 بلاغان مؤكدان اتصلحوا هنا:
 *   • **عنوان الجدول كان بيكدب**: «الطلبات المفتوحة» والمعروض كل الطلبات
 *     بما فيها المقفولة ⇒ العنوان بقى تابع للفلتر النشط حرفيًا.
 *   • **الترتيب كان بيكدب**: «مرتّب بالأقدم انتظارًا» والصفوف مش مرتّبة ⇒
 *     المسؤولة بتتصل بالأحدث وتسيب اللي مستني من 12 يوم. دلوقتي الترتيب
 *     بيتطلب وبيتأكّد، ولو ما اتأكّدش الجملة نفسها بتتغيّر (api/leads.ts).
 *
 * ⚠️ ومجموع الفلاتر لازم يساوي «الكل» — الاختلاف بيتقال للمستخدم مش بيتخبّى.
 * ⛔ فعل أساسي كحلي **واحد**: الفعل بتاع أول طلب مستني تواصل.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import { Icon } from "@/components/Icon";
import {
  EmptyState,
  ErrorState,
  FirstUseState,
  LoadingState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Ltr, fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import { useSession } from "@/lib/session";
import {
  ActionNotHereError,
  BULK_LIMIT,
  LEAD_SEGMENTS,
  bulkRemind,
  bulkReassign,
  fetchFunnel,
  fetchLeads,
  fetchPortalStatus,
  isManualSource,
  languageLabelAr,
  leadTone,
  runLeadAction,
  closeLead,
  convertLead,
  fetchConvertPreview,
  TIMEZONE_OPTIONS,
  CLOSE_OUTCOMES,
  type CloseOutcome,
  sourceLabelAr,
  createManualLead,
  MANUAL_SOURCES,
  type ManualLeadInput,
  type Funnel,
  type LeadRow,
  type LeadSegment,
  type LeadsPage,
  type PortalStatus,
} from "@/api/leads";
import { fetchMembers, type TeamMember } from "@/api/team";
import { COUNTRY_OPTIONS } from "@/api/registry";

const PER_PAGE = 7;
/** الأفعال اللي تستاهل الزرار الكحلي — اللي لسه محتاج تواصل */
const URGENT_ACTIONS = new Set(["contact", "claim"]);
const BULK_REASON = "إعادة توزيع جماعية من شاشة الليدز";

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; value: LeadsPage }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** الحالات اللي الطلب فيها مقفول خلاص ⇒ زرار الإقفال ما يظهرش.
 *  ⚠️ مطابق لحارس `services/leads.py::close_lead` — لو اتغيّر هناك، غيّره هنا. */
const CLOSED_STATUSES = new Set(["converted", "lost", "never_completed"]);

export function Leads() {
  const { navigate } = useRouter();
  const { me } = useSession();
  const dept = me?.user.department ?? null;

  const [segment, setSegment] = useState<LeadSegment>("all");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [funnelError, setFunnelError] = useState<string | null>(null);
  const [portal, setPortal] = useState<PortalStatus | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [target, setTarget] = useState<string>("");
  const [confirmRemind, setConfirmRemind] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const value = await fetchLeads({ segment, page, perPage: PER_PAGE });
      setState({ kind: "ready", value });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت
      console.error("[leads] تحميل الطلبات فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [segment, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // التحديد بيتصفّى مع أي تغيير في الصفحة أو الفلتر — ⛔ ممنوع فعل جماعي
  // على صفوف الموظف مش شايفها
  useEffect(() => {
    setSelected([]);
    setConfirmRemind(false);
    setBulkError(null);
  }, [segment, page]);

  useEffect(() => {
    let alive = true;
    fetchFunnel("week").then(
      (v) => {
        if (alive) setFunnel(v);
      },
      (err: unknown) => {
        console.error("[leads] «قمع الأسبوع» فشل", err);
        if (alive) setFunnelError(errorMessageAr(err));
      },
    );
    fetchPortalStatus().then(
      (v) => {
        if (alive) setPortal(v);
      },
      (err: unknown) => {
        console.error("[leads] حالة الربط بفورم الموقع مش معروفة", err);
        if (alive) setPortalError(errorMessageAr(err));
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchMembers(dept).then(
      (rows) => {
        if (alive) setMembers(rows.filter((m) => m.can_receive));
      },
      (err: unknown) => {
        console.error("[leads] قايمة الفريق مش متاحة — إعادة التوزيع الجماعية هتتقفل", err);
        if (alive) setMembersError(errorMessageAr(err));
      },
    );
    return () => {
      alive = false;
    };
  }, [dept]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  function showToast(text: string) {
    setToast(text);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }

  /** الطلب اللي بيتقفل دلوقتي — `null` يعني اللوحة مقفولة */
  // ── «إضافة طلب يدوي» ──
  // ⭐ الطلبات الجاية واتساب أو بمكالمة. ربط الواتساب المباشر محتاج API
  //    رسمي مكلّف، والطريق غير الرسمي معرّض إن رقم الأكاديمية يتوقف —
  //    فالقرار المعتمد إن الإدخال يدوي، بس **جوّه السيستم** مش في نوتة.
  // ⛔ خانتين إجباريتين وبس (اسم + رقم). الموظفة بتدخّل وهي في نص
  //    مكالمة — ولو سألناها عن ٨ خانات، الطلب ما بيتسجّلش خالص.
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addSource, setAddSource] = useState<ManualLeadInput["source"]>("whatsapp");
  const [addNote, setAddNote] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const [closing, setClosing] = useState<LeadRow | null>(null);
  const [closeOutcome, setCloseOutcome] = useState<CloseOutcome>("lost");
  const [closeReason, setCloseReason] = useState("");
  const [closeError, setCloseError] = useState<string | null>(null);

  function resetAdd() {
    setAdding(false);
    setAddName("");
    setAddPhone("");
    setAddSource("whatsapp");
    setAddNote("");
    setAddError(null);
  }

  async function onSubmitManualLead() {
    if (busy) return;
    const name = addName.trim();
    const phone = addPhone.trim();
    // ⚠️ نفس حدود السيرفر — الرفض هنا أسرع وأوضح من 422
    if (name.length < 2) {
      setAddError("اكتب اسم الطالب أو ولي الأمر");
      return;
    }
    if (!/^\+?[\d\s()-]{6,}$/.test(phone)) {
      setAddError("اكتب الرقم بكود الدولة — مثال: +20 أو +1");
      return;
    }
    setBusy("manual-lead");
    setAddError(null);
    try {
      const out = await createManualLead({
        studentName: name, phone, source: addSource, entryNoteAr: addNote,
      });
      // 🔑 التكرار **مش فشل**: الطلب القديم موجود والعدّاد زاد. بنقفل
      //    اللوحة ونقول اللي حصل بالنص، عشان ما تفتكرش إن إدخالها ضاع.
      resetAdd();
      showToast(out.toastAr);
      await load();
    } catch (err) {
      console.error("[leads] إضافة طلب يدوي فشلت", err);
      setAddError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  /* ═══ تحويل الطلب لأسرة وطلاب ═══ */
  const [converting, setConverting] = useState<LeadRow | null>(null);
  const [cvFamily, setCvFamily] = useState("");
  const [cvTz, setCvTz] = useState("");
  const [cvCountry, setCvCountry] = useState("");
  const [cvChildren, setCvChildren] = useState<{ nameAr: string; age: string }[]>([]);
  const [cvFlags, setCvFlags] = useState<string[]>([]);
  const [cvError, setCvError] = useState<string | null>(null);

  async function openConvert(row: LeadRow) {
    setClosing(null);
    setConverting(row);
    setCvError(null);
    setBusy(row.id);
    try {
      const pv = await fetchConvertPreview(row.id);
      setCvFamily(pv.familyNameAr);
      setCvTz(pv.timezone ?? "");
      setCvCountry(pv.country ?? "");
      setCvChildren(
        pv.students.length > 0
          ? pv.students.map((st) => ({ nameAr: st.nameAr, age: st.age ? String(st.age) : "" }))
          : [{ nameAr: row.display_name_ar, age: "" }],
      );
      setCvFlags(pv.flags);
    } catch (err) {
      setCvError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  async function onConfirmConvert() {
    if (!converting || busy) return;
    const family = cvFamily.trim();
    const kids = cvChildren.map((c) => ({ nameAr: c.nameAr.trim(), age: Number(c.age) || null })).filter((c) => c.nameAr);
    if (family.length < 2) {
      setCvError("اكتب اسم الأسرة.");
      return;
    }
    if (!cvTz) {
      setCvError("اختار المنطقة الزمنية — من غيرها موعد التقييم هيتكتب بتوقيت غلط.");
      return;
    }
    if (!cvCountry) {
      setCvError("اختار الدولة — خطوة استقبال البيانات مش بتتقفل من غيرها.");
      return;
    }
    if (kids.length === 0) {
      setCvError("اكتب اسم طالب واحد على الأقل.");
      return;
    }
    setBusy(converting.id);
    setCvError(null);
    try {
      const out = await convertLead(converting.id, { familyNameAr: family, timezone: cvTz, country: cvCountry, children: kids });
      setConverting(null);
      showToast(out.toastAr);
      await load();
    } catch (err) {
      console.error(`[leads] تحويل الطلب ${converting.id} فشل`, err);
      setCvError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  async function onConfirmClose() {
    if (!closing || busy) return;
    const reason = closeReason.trim();
    // ⚠️ نفس الحد الأدنى بتاع السيرفر — الرفض هنا أسرع وأوضح من 422
    if (reason.length < 3) {
      setCloseError("اكتب سبب الإقفال — الطلب اللي بيتقفل بلا سبب محدش بيرجعله");
      return;
    }
    setBusy(closing.id);
    setCloseError(null);
    try {
      const out = await closeLead(closing.id, closeOutcome, reason);
      setClosing(null);
      setCloseReason("");
      showToast(out.toastAr);
      await load();
    } catch (err) {
      console.error(`[leads] إقفال الطلب ${closing.id} فشل`, err);
      setCloseError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  async function onRowAction(row: LeadRow) {
    if (busy) return;
    // 🔑 التحويل لأسرة بيتعمل من لوحة جوّه الشاشة — مش طلب مباشر
    if (row.next_action?.key === "convert") {
      await openConvert(row);
      return;
    }
    setBusy(row.id);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    try {
      const out = await runLeadAction(row);
      if (out.href) {
        navigate(out.href);
        return;
      }
      showToast(out.toastAr);
      await load();
    } catch (err) {
      console.error(`[leads] الفعل على الطلب ${row.id} فشل`, err);
      const messageAr =
        err instanceof ActionNotHereError ? err.messageAr : errorMessageAr(err);
      setRowErrors((prev) => ({ ...prev, [row.id]: messageAr }));
    } finally {
      setBusy(null);
    }
  }

  async function onBulkReassign() {
    if (busy || !target) return;
    setBusy("bulk");
    setBulkError(null);
    try {
      const n = await bulkReassign(selected, target, BULK_REASON);
      const name = members.find((m) => m.id === target)?.name_ar ?? "المسؤول الجديد";
      showToast(`${fmtNum(n)} طلب اتنقلوا لـ${name}`);
      setSelected([]);
      await load();
    } catch (err) {
      console.error("[leads] إعادة التوزيع الجماعية فشلت", err);
      setBulkError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  async function onBulkRemind() {
    if (busy) return;
    // خطوتين عمدًا: التذكير الجماعي بيروح لأولياء أمور حقيقيين
    if (!confirmRemind) {
      setConfirmRemind(true);
      return;
    }
    setBusy("bulk");
    setBulkError(null);
    try {
      const n = await bulkRemind(selected, "whatsapp");
      showToast(`التذكير اتبعت لـ${fmtNum(n)} طلب`);
      setSelected([]);
      setConfirmRemind(false);
      await load();
    } catch (err) {
      console.error("[leads] التذكير الجماعي فشل", err);
      setBulkError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  const data = state.kind === "ready" ? state.value : null;
  const rows = data?.rows ?? [];
  const active = LEAD_SEGMENTS.find((s) => s.key === segment) ?? LEAD_SEGMENTS[0];
  const total = data?.total ?? 0;
  // ⚠️ الترقيم من رد السيستم مش من الحالة المحلية — لو السيستم صحّح الصفحة
  //    (زي طلب صفحة مش موجودة) لازم «عرض 1–7 من 31» تقول الحقيقة.
  const shownPage = data?.page ?? page;
  const shownPerPage = data?.perPage ?? PER_PAGE;
  const firstIndex = total === 0 ? 0 : (shownPage - 1) * shownPerPage + 1;
  const lastIndex = total === 0 ? 0 : firstIndex + rows.length - 1;
  const totalPages = data?.totalPages ?? 1;
  // ⛔ زرار كحلي واحد بس: أول طلب مستني تواصل (في «محتاجة متابعة» ده الأقدم انتظارًا)
  const primaryId =
    rows.find((r) => r.next_action && URGENT_ACTIONS.has(r.next_action.key))?.id ?? null;
  const allChecked = rows.length > 0 && selected.length === rows.length;

  const crumb = data ? (
    <>
      <span className="num">{fmtNum(total)}</span> طلب في «{active?.chip}» ·{" "}
      {data.sortTrusted
        ? data.newestFirst
          ? "الأحدث الأول"
          : "الأقدم انتظارًا الأول"
        : "الترتيب زي ما رجع من السيستم"}
    </>
  ) : null;

  return (
    <Shell title="الليدز" {...(crumb ? { crumb } : {})}>
      {/* ═══ (1) شريط علوي: حالة الاتصال + فعل ثانوي ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <PortalChip status={portal} errorAr={portalError} />
        <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span className="t-meta">جاك طلب واتساب أو بمكالمة؟</span>
          <button
            type="button"
            className="btn sm accent"
            aria-expanded={adding}
            onClick={() => (adding ? resetAdd() : setAdding(true))}
          >
            + إضافة طلب يدوي
          </button>
        </div>
      </div>

      {/* ═══ (1ب) لوحة «إضافة طلب يدوي» ═══ */}
      {adding ? (
        <div
          className="card"
          role="dialog"
          aria-label="إضافة طلب يدوي"
          style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10,
                   marginTop: 12 }}
        >
          <span className="sec-title">طلب جه واتساب أو بمكالمة</span>
          <span className="t-meta">
            الطلب بياخد نفس الرحلة ونفس المهام زي أي طلب من فورم الموقع
          </span>

          <input
            type="text"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="اسم الطالب أو ولي الأمر"
            aria-label="اسم الطالب أو ولي الأمر"
            style={{ font: "inherit", color: "var(--ink)",
                     background: "var(--surface)", border: "1px solid var(--line)",
                     borderRadius: "var(--r-control)", padding: "8px 10px",
                     width: "100%" }}
          />

          {/* dir="ltr" مسموح: رقم لاتيني صافي بلا أي حرف عربي */}
          <input
            type="tel"
            dir="ltr"
            value={addPhone}
            onChange={(e) => setAddPhone(e.target.value)}
            placeholder="+20 100 000 0000"
            aria-label="رقم التليفون بكود الدولة"
            style={{ font: "inherit", color: "var(--ink)",
                     background: "var(--surface)", border: "1px solid var(--line)",
                     borderRadius: "var(--r-control)", padding: "8px 10px",
                     width: "100%", textAlign: "left" }}
          />

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {MANUAL_SOURCES.map((src) => (
              <label
                key={src.key}
                style={{ display: "flex", alignItems: "center", gap: 6,
                         cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="manual-lead-source"
                  checked={addSource === src.key}
                  onChange={() => setAddSource(src.key)}
                />
                <span>{src.label}</span>
              </label>
            ))}
          </div>

          <textarea
            rows={2}
            value={addNote}
            onChange={(e) => setAddNote(e.target.value)}
            placeholder="طلبه إيه بالظبط؟ (اختياري) — مثال: عايزة تحفيظ لبنتين بعد العصر"
            aria-label="ملاحظة عن الطلب"
            style={{ font: "inherit", color: "var(--ink)",
                     background: "var(--surface)", border: "1px solid var(--line)",
                     borderRadius: "var(--r-control)", padding: "8px 10px",
                     width: "100%", resize: "vertical" }}
          />

          {addError ? (
            <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
              {addError}
            </span>
          ) : null}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={`btn sm accent${busy === "manual-lead" ? " is-busy" : ""}`}
              disabled={busy !== null}
              aria-busy={busy === "manual-lead"}
              onClick={() => void onSubmitManualLead()}
            >
              {busy === "manual-lead" ? "بيتسجّل…" : "سجّل الطلب"}
            </button>
            <button
              type="button"
              className="btn sm ghost"
              disabled={busy !== null}
              onClick={resetAdd}
            >
              إلغاء
            </button>
          </div>
        </div>
      ) : null}

      {/* ═══ (2) قمع الأسبوع ═══ */}
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="sec-h">
          <span className="sec-title">قمع الأسبوع</span>
        </div>
        {funnel ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 1fr) 72px minmax(0, 1fr) 72px minmax(0, 1fr) 72px minmax(0, 1fr)",
                alignItems: "center",
              }}
            >
              <FunnelStage n={funnel.arrived} label="وصلوا" />
              <FunnelArrow rate={funnel.rates.contacted} />
              <FunnelStage n={funnel.contacted} label="تم التواصل" />
              <FunnelArrow rate={funnel.rates.evaluation_booked} />
              <FunnelStage n={funnel.evaluation_booked} label="حجزوا تقييم" />
              <FunnelArrow rate={funnel.rates.completed} />
              <FunnelStage n={funnel.completed} label="اكتملوا" />
            </div>
            {funnel.biggestDropAr ? (
              <div className="t-desc" style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                {latinDigits(funnel.biggestDropAr)}
              </div>
            ) : null}
          </>
        ) : funnelError ? (
          <div className="t-desc">مقدرناش نجيب قمع الأسبوع — {funnelError}</div>
        ) : (
          <LoadingState rows={2} />
        )}
      </div>

      {/* ═══ (3) الفلاتر — مجموعها لازم يساوي «الكل» ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {LEAD_SEGMENTS.map((s) => {
          const n = data?.counts[s.key] ?? null;
          return (
            <button
              key={s.key}
              type="button"
              className={`filt${segment === s.key ? " on" : ""}`}
              aria-pressed={segment === s.key}
              onClick={() => {
                setSegment(s.key);
                setPage(1);
              }}
            >
              {s.chip} {n === null ? null : <span className="num">{fmtNum(n)}</span>}
            </button>
          );
        })}
        {data?.countsConsistent === false ? (
          <span className="chip soon" role="alert">
            <span className="bullet" />
            مجموع الفلاتر مش مطابق «الكل» — الأرقام دي محتاجة مراجعة
          </span>
        ) : null}
      </div>

      {/* 🔑 الشاشة بتقول تعريف شريحتها الأولى — ده نص إصلاح بلاغ «فيه شاشتين
          بنفس اسم الشريحة». شاشة الإشراف الإداري بتعمل نفس الحاجة، والتناظر
          بينهم هو اللي بيمنع رجوع اللبس. */}
      {active?.defAr ? (
        <span className="t-meta">
          «{active.chip}» = {active.defAr}
        </span>
      ) : null}

      {/* ═══ (4) الجدول — العنوان تابع للفلتر ═══ */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
          <span className="sec-title">{active?.title}</span>
          <span className="t-meta">
            {data?.sortTrusted
              ? data.newestFirst
                ? "مرتّب بالأحدث — اللي لسه واصل فوق"
                : "مرتّب بالأقدم انتظارًا"
              : "الترتيب زي ما رجع من السيستم — مش مضمون"}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}

        {state.kind === "ready" && rows.length === 0 && segment === "all" ? (
          <FirstUseState
            title="لسه مفيش طلبات"
            hint="هيظهر أول ما حد يملأ فورم الموقع"
            connected={portal !== null && portal.mode !== "manual"}
          />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && segment !== "all" ? (
          <EmptyState
            title={`مفيش طلبات في «${active?.chip}»`}
            hint="جرّب فلتر تاني أو شوف «الكل»"
            action={{ label: "عرض الكل", onClick: () => setSegment("all") }}
          />
        ) : null}

        {rows.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 44, paddingLeft: 0 }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      aria-label="تحديد كل الصفوف الظاهرة"
                      style={{ width: 16, height: 16, accentColor: "var(--blue)" }}
                      onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])}
                    />
                  </th>
                  <th>الأسرة</th>
                  <th>اللغة والمصدر</th>
                  <th>وصل منذ</th>
                  <th>الحالة</th>
                  <th>المسؤول</th>
                  <th>الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="row-link">
                    <td style={{ width: 44, paddingLeft: 0 }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        aria-label={`تحديد ${row.display_name_ar}`}
                        style={{ width: 16, height: 16, accentColor: "var(--blue)" }}
                        onChange={(e) =>
                          setSelected((cur) =>
                            e.target.checked
                              ? [...cur, row.id]
                              : cur.filter((id) => id !== row.id),
                          )
                        }
                      />
                    </td>
                    <td>
                      <div className="t-body clip">{row.display_name_ar}</div>
                      <div className="t-meta">
                        {row.phone_masked ? <Ltr>{row.phone_masked}</Ltr> : "بلا رقم"}
                      </div>
                    </td>
                    <td>
                      <span className="chip ghost">
                        {languageLabelAr(row.language)} — {sourceLabelAr(row.source)}
                      </span>
                      {isManualSource(row.source) ? (
                        <span
                          className="src"
                          style={{ marginRight: 6 }}
                          title="القناة دي مالهاش مدخل آلي — الفريق بيسجّلها بإيده"
                        >
                          إدخال يدوي
                        </span>
                      ) : null}
                    </td>
                    <td className="t-desc">
                      {daysLabel(row.waiting_days)}
                      {/* 🔑 الأسرة بعتت تاني وهي مستنية — الرقم ده أقوى من
                          مدة الانتظار لوحدها في ترتيب الأولوية */}
                      {row.resubmit_count ? (
                        <span
                          className="chip late"
                          style={{ marginRight: 6 }}
                          title="الأسرة ملت الفورم تاني وهي مستنية رد"
                        >
                          <span className="bullet" />
                          بعت تاني
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`chip ${leadTone(row)}`}>
                        <span className="bullet" />
                        {latinDigits(row.status_label_ar)}
                      </span>
                    </td>
                    <td className="t-desc">{row.assignee_name_ar ?? "مش متسند"}</td>
                    <td>
                      {row.next_action ? (
                        <button
                          type="button"
                          className={`btn sm ${row.id === primaryId ? "primary" : "ghost"}${
                            busy === row.id ? " is-busy" : ""
                          }`}
                          disabled={busy !== null}
                          onClick={() => void onRowAction(row)}
                        >
                          {busy === row.id ? "بيتنفّذ…" : row.next_action.label_ar}
                        </button>
                      ) : (
                        <span className="t-meta">مفيش إجراء</span>
                      )}
                      {/* ⚠️ ثانوي دايمًا (`ghost`) — الشاشة ليها فعل أساسي
                          واحد بس، والإقفال مش هو. */}
                      {CLOSED_STATUSES.has(row.status) ? null : (
                        <button
                          type="button"
                          className="btn sm ghost"
                          style={{ marginRight: 6 }}
                          disabled={busy !== null}
                          onClick={() => {
                            setClosing(row);
                            setCloseOutcome("lost");
                            setCloseReason("");
                            setCloseError(null);
                          }}
                        >
                          إقفال
                        </button>
                      )}
                      {rowErrors[row.id] ? (
                        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert">
                          {rowErrors[row.id]}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* ═══ لوحة تحويل الطلب لأسرة وطلاب ═══ */}
      {converting ? (
        <div
          className="card"
          role="dialog"
          aria-label="تحويل الطلب لأسرة وطلاب"
          style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}
        >
          <span className="sec-title">
            تحويل الطلب لأسرة وطلاب — <bdi>{converting.display_name_ar}</bdi>
          </span>
          <span className="t-desc">
            دي الخطوة اللي بتعمل الأسرة والطلاب في السيستم. بعدها بتقدر تحجز التقييم من الرحلة.
          </span>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="t-meta">اسم الأسرة (زي ما هيظهر في سجل الطلاب)</span>
            <input
              type="text"
              value={cvFamily}
              onChange={(e) => setCvFamily(e.target.value)}
              style={{ font: "inherit", color: "var(--ink)", background: "var(--surface)",
                       border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "8px 10px" }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="t-meta">الدولة</span>
            <select
              value={cvCountry}
              onChange={(e) => setCvCountry(e.target.value)}
              style={{ font: "inherit", color: "var(--ink)", background: "var(--surface)",
                       border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "8px 10px" }}
            >
              <option value="">— اختار الدولة —</option>
              {COUNTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label_ar}</option>
              ))}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="t-meta">المنطقة الزمنية للأسرة — موعد التقييم بيتكتب بيها</span>
            <select
              value={cvTz}
              onChange={(e) => setCvTz(e.target.value)}
              style={{ font: "inherit", color: "var(--ink)", background: "var(--surface)",
                       border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "8px 10px" }}
            >
              <option value="">— اختار بلد الأسرة —</option>
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="t-meta">الطلاب (الاسم والسن)</span>
            {cvChildren.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  value={c.nameAr}
                  placeholder="اسم الطالب"
                  onChange={(e) => setCvChildren((prev) => prev.map((x, j) => (j === i ? { ...x, nameAr: e.target.value } : x)))}
                  style={{ flex: 1, font: "inherit", color: "var(--ink)", background: "var(--surface)",
                           border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "8px 10px" }}
                />
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={c.age}
                  placeholder="السن"
                  onChange={(e) => setCvChildren((prev) => prev.map((x, j) => (j === i ? { ...x, age: e.target.value } : x)))}
                  style={{ width: 80, font: "inherit", color: "var(--ink)", background: "var(--surface)",
                           border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "8px 10px" }}
                />
                {cvChildren.length > 1 ? (
                  <button type="button" className="btn sm ghost" onClick={() => setCvChildren((prev) => prev.filter((_, j) => j !== i))}>
                    شيل
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className="btn sm ghost"
              style={{ alignSelf: "flex-start" }}
              onClick={() => setCvChildren((prev) => [...prev, { nameAr: "", age: "" }])}
            >
              + طالب تاني من نفس الأسرة
            </button>
          </div>

          {cvFlags.length > 0 ? (
            <span className="t-meta">هيتعلّم عليه «محتاج استكمال»: {cvFlags.join(" · ")} — تكمّله بعدين من سجل الطالب.</span>
          ) : null}

          {cvError ? (
            <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
              {cvError}
            </span>
          ) : null}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={`btn sm primary${busy ? " is-busy" : ""}`}
              disabled={busy !== null}
              aria-busy={busy !== null}
              onClick={() => void onConfirmConvert()}
            >
              {busy ? "بيتحوّل…" : "اعمل الأسرة والطلاب وافتح الرحلة"}
            </button>
            <button type="button" className="btn sm ghost" disabled={busy !== null} onClick={() => setConverting(null)}>
              رجوع
            </button>
          </div>
        </div>
      ) : null}

      {/* ═══ لوحة إقفال الطلب ═══
          🔴 كل خيار مكتوب تحته **بيعمل إيه بالرحلة** — الموظف لازم يعرف إن
             الإقفال بيلغي مهام **قبل** ما يضغط مش بعدين.
          ⚠️ ستايل سطري بنفس نمط `Desk.tsx::AddTaskForm` — مفيش كلاسات
             جديدة عشان ما نضيفش سطحًا للتصميم لأجل لوحة واحدة. */}
      {closing ? (
        <div
          className="card"
          role="dialog"
          aria-label="إقفال الطلب"
          style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10,
                   marginTop: 12 }}
        >
          <span className="sec-title">
            إقفال الطلب — <bdi>{closing.display_name_ar}</bdi>
          </span>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {CLOSE_OUTCOMES.map((o) => (
              <label
                key={o.key}
                style={{ display: "flex", alignItems: "flex-start", gap: 8,
                         cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="close-outcome"
                  checked={closeOutcome === o.key}
                  onChange={() => setCloseOutcome(o.key)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  {o.labelAr}
                  <span className="t-meta"> — {o.hintAr}</span>
                </span>
              </label>
            ))}
          </div>

          <textarea
            rows={2}
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            placeholder="سبب الإقفال — مثال: الأسرة قالت مش هتكمل دلوقتي"
            aria-label="سبب الإقفال"
            style={{ font: "inherit", color: "var(--ink)",
                     background: "var(--surface)", border: "1px solid var(--line)",
                     borderRadius: "var(--r-control)", padding: "8px 10px",
                     width: "100%", resize: "vertical" }}
          />

          {closeError ? (
            <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
              {closeError}
            </span>
          ) : null}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={`btn sm ghost${busy ? " is-busy" : ""}`}
              disabled={busy !== null}
              aria-busy={busy !== null}
              onClick={() => void onConfirmClose()}
            >
              {busy ? "بيتقفل…" : "أقفل الطلب"}
            </button>
            <button
              type="button"
              className="btn sm ghost"
              disabled={busy !== null}
              onClick={() => setClosing(null)}
            >
              رجوع
            </button>
          </div>
        </div>
      ) : null}

      {/* ═══ (5) الترقيم ═══ */}
      {rows.length > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className="t-meta">
            عرض <span className="num">{fmtNum(firstIndex)}</span>–
            <span className="num">{fmtNum(lastIndex)}</span> من{" "}
            <span className="num">{fmtNum(total)}</span>
          </span>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage <= 1 || state.kind === "loading"}
            onClick={() => setPage(Math.max(1, shownPage - 1))}
          >
            السابق
          </button>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage >= totalPages || state.kind === "loading"}
            onClick={() => setPage(Math.min(totalPages, shownPage + 1))}
          >
            التالي
          </button>
          <span className="t-meta" style={{ marginRight: "auto" }}>
            التحديد المتعدد بيتيح إعادة توزيع أو تذكير جماعي — وبيتصفّى مع تغيير الصفحة
          </span>
        </div>
      ) : null}

      {/* ═══ (6) شريط التحديد المتعدد ═══ */}
      {selected.length > 0 ? (
        <div
          className="card"
          style={{ padding: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
        >
          <span className="t-body">
            متحدّد <span className="num">{fmtNum(selected.length)}</span> طلب
          </span>
          <button type="button" className="link" onClick={() => setSelected([])}>
            إلغاء التحديد
          </button>

          <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {membersError ? (
              <span className="t-meta">قايمة الفريق مش متاحة — {membersError}</span>
            ) : (
              <select
                className="filt"
                value={target}
                aria-label="المسؤول الجديد"
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">اختار المسؤول الجديد…</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name_ar}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className={`btn sm ${target && selected.length <= BULK_LIMIT ? "ghost" : "is-off"}${
                busy === "bulk" ? " is-busy" : ""
              }`}
              disabled={busy !== null || !target || selected.length > BULK_LIMIT}
              onClick={() => void onBulkReassign()}
            >
              إعادة توزيع
            </button>
            <button
              type="button"
              className={`btn sm ${selected.length <= BULK_LIMIT ? "ghost" : "is-off"}${
                busy === "bulk" ? " is-busy" : ""
              }`}
              disabled={busy !== null || selected.length > BULK_LIMIT}
              onClick={() => void onBulkRemind()}
            >
              {confirmRemind
                ? `تأكيد إرسال التذكير لـ ${fmtNum(selected.length)}`
                : "تذكير جماعي (واتساب)"}
            </button>
          </div>

          {selected.length > BULK_LIMIT ? (
            <span className="t-meta" style={{ color: "var(--danger)" }} role="alert">
              الحد <span className="num">{fmtNum(BULK_LIMIT)}</span> طلب في المرة — قلّل التحديد
            </span>
          ) : null}
          {bulkError ? (
            <span className="t-meta" style={{ color: "var(--danger)" }} role="alert">
              {bulkError}
            </span>
          ) : null}
        </div>
      ) : null}

      {toast ? (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: "50%",
            transform: "translateX(50%)",
            zIndex: 40,
          }}
        >
          <div className="toast" role="status">
            <Icon name="check" size={14} stroke="#FFFFFF" strokeWidth={2.2} />
            {toast}
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

/* ─────────────────────── مكوّنات الشاشة ─────────────────────── */

/**
 * ⚠️ التصميم كان بيقول «متصل بفورم الموقع» على طول. الشاشة مش هتدّعي
 *    اتصال ما اتأكّدش — الحالة المجهولة بتتقال زي ما هي.
 */
function PortalChip({ status, errorAr }: { status: PortalStatus | null; errorAr: string | null }) {
  if (status) {
    const connected = status.mode !== "manual";
    return (
      <span className={`chip ${connected ? "okc" : "ghost"}`}>
        <span className="bullet" />
        {status.messageAr ||
          (connected
            ? "متصل بفورم الموقع — الطلب بيوصل ويتسجّل لوحده"
            : "فورم الموقع مش مربوط — التسجيل يدوي")}
      </span>
    );
  }
  return (
    <span className="chip ghost" title={errorAr ?? undefined}>
      <span className="bullet" />
      {errorAr ? "حالة الربط بفورم الموقع مش معروفة" : "بنشوف حالة الربط بفورم الموقع…"}
    </span>
  );
}

function FunnelStage({ n, label }: { n: number; label: string }) {
  return (
    <div
      className="stat"
      style={{ boxShadow: "none", padding: 0, alignItems: "center", textAlign: "center", background: "transparent", cursor: "default" }}
    >
      <div className="v">
        <span className="num">{fmtNum(n)}</span>
      </div>
      <div className="l">{label}</div>
    </div>
  );
}

function FunnelArrow({ rate }: { rate: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width="26" height="10" viewBox="0 0 26 10" fill="none" stroke="#98A2B3" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M25 5H2" />
        <path d="M6.5 1 2 5l4.5 4" />
      </svg>
      <div className="t-meta">
        {rate ? (
          <>
            <span className="num">{latinDigits(rate)}</span>٪
          </>
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── أدوات ─────────────────────── */

/** «12 يومًا» · «يومين» · «5 أيام» — والمجهول «—» مش صفر مخترع */
function daysLabel(days: number | null): string {
  if (days === null) return "—";
  if (days <= 0) return "النهارده";
  if (days === 1) return "يوم";
  if (days === 2) return "يومين";
  if (days <= 10) return `${fmtNum(days)} أيام`;
  return `${fmtNum(days)} يومًا`;
}

/** حارس: أي رقم عربي-هندي جاي من السيستم بيتحوّل للاتيني — قاعدة مقفولة */
function latinDigits(text: string): string {
  return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}
